// src/pages/home/index.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Paper,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getSubscriptionPackages } from "../../services/subscriptionService";
import subscriptionHistoryService from "../../services/subscriptionHistoryService";
import { toast } from "react-toastify";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { USERMGMT_URL } from "../../utils/envConfig";
import {
  STANDARD_FORM_DIALOG_PAPER_SX,
  STANDARD_FORM_DIALOG_TITLE_SX,
  STANDARD_FORM_DIALOG_ACTIONS_SX,
  CASH_PAYMENT_DIALOG_PAPER_SX,
  FIT_CONTENT_DIALOG_CONTENT_SX,
  FORM_CANCEL_BUTTON_SX,
  FORM_SUBMIT_BUTTON_SX,
  DENSE_FIELD_PROPS,
} from "../../utils/formModalStyles";

const HMS_BLUE = "#0B78D0";
const HMS_BLUE_DARK = "#0866B3";
const HMS_BLUE_LIGHT = "#EAF4FC";
const HMS_PAGE_BG = "#F5F7FA";
const HMS_TEXT = "#2F3A4A";
const HMS_TEXT_GREY = "#6B7280";
const HMS_BORDER = "#E5E9EF";

const packageColors = {
  BASIC: "#4CAF50",
  INTERMEDIATE: "#2196F3",
  PREMIUM: "#FF9800",
};

const HomePage = () => {
  const [selectedPackage, setSelectedPackage] = useState("basic");
  const [packageType, setPackageType] = useState("YEARLY"); // MONTHLY, QUARTERLY, YEARLY
  const [paymentMethod, setPaymentMethod] = useState("online");
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const navigate = useNavigate();
  
  // Cash payment authentication states
  const [showCashPaymentDialog, setShowCashPaymentDialog] = useState(false);
  const [showSuperAdminAuth, setShowSuperAdminAuth] = useState(false);
  const [cashPaymentDetails, setCashPaymentDetails] = useState({
    payerName: "",
    payerMobile: "",
    recipientName: "",
    recipientMobile: "",
    paymentComment: "",
    paymentProofName: "",
    paymentProofData: "",
  });
  const [superAdminCredentials, setSuperAdminCredentials] = useState({
    username: "",
    password: "",
  });
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState("");

  // Fetch subscription types from API
  useEffect(() => {
    const fetchSubscriptionTypes = async () => {
      try {
        setLoadingPackages(true);
        const subscriptionTypes = await subscriptionHistoryService.getSubscriptionTypes();
        
        // Transform subscription types to match the expected format
        const transformedPackages = subscriptionTypes.map((type) => {
          // Handle capabilities - can be JSON string or array
          let features = [];
          if (typeof type.capabilities === 'string') {
            try {
              features = JSON.parse(type.capabilities);
            } catch (e) {
              features = [];
            }
          } else if (Array.isArray(type.capabilities)) {
            features = type.capabilities;
          }
          
          return {
            id: type.subType?.toLowerCase() || type.id?.toString(),
            name: type.subType || "Unknown",
            basePrice: parseFloat(type.packageAmount || 0),
            features: features,
            color: packageColors[type.subType] || "#757575",
            subscriptionTypeId: type.id,
            maxUsers: type.maxUsersAllowed === -1 ? -1 : type.maxUsersAllowed,
          };
        });
        
        setPackages(transformedPackages);
        
        // Set default selected package if none is selected
        if (transformedPackages.length > 0) {
          const currentPackage = sessionStorage.getItem("selectedPackage");
          if (!currentPackage || !transformedPackages.find(p => p.id === currentPackage)) {
            setSelectedPackage(transformedPackages[0].id);
          }
        }
      } catch (error) {
        console.error("Error fetching subscription types:", error);
        toast.error("Failed to load subscription packages. Please refresh the page.");
        // Fallback to empty array or default packages if needed
        setPackages([]);
      } finally {
        setLoadingPackages(false);
      }
    };
    
    fetchSubscriptionTypes();
  }, []);

  // Calculate price based on package type (monthly, quarterly, yearly)
  const calculatePrice = (basePrice, pkgType) => {
    switch (pkgType) {
      case "MONTHLY":
        return basePrice; // Base price is monthly
      case "QUARTERLY":
        return basePrice * 3; // 3 months
      case "YEARLY":
        return basePrice * 12; // 12 months
      default:
        return basePrice;
    }
  };

  // Get selected package details
  const selectedPackageDetails = packages.find(pkg => pkg.id === selectedPackage);

  const handleGetStarted = async () => {
    if (!selectedPackage) {
      toast.error("Please select a subscription package");
      return;
    }

    // If cash payment, show cash payment details dialog first
    if (paymentMethod === "cash") {
      setShowCashPaymentDialog(true);
      return;
    }

    // For online payment, proceed directly to onboarding
    setLoading(true);
    try {
      // Store selected package, package type, and payment method in sessionStorage
      sessionStorage.setItem("selectedPackage", selectedPackage);
      sessionStorage.setItem("packageType", packageType);
      sessionStorage.setItem("paymentMethod", paymentMethod);

      // Navigate to onboarding
      navigate("/onboarding");
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to proceed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCashPaymentDetailsSubmit = () => {
    // Validate cash payment details
    if (!cashPaymentDetails.payerName.trim()) {
      toast.error("Please enter payer name");
      return;
    }
    if (!cashPaymentDetails.payerMobile.trim() || cashPaymentDetails.payerMobile.length !== 10) {
      toast.error("Please enter valid payer mobile number (10 digits)");
      return;
    }
    if (!cashPaymentDetails.recipientName.trim()) {
      toast.error("Please enter recipient name");
      return;
    }
    if (!cashPaymentDetails.recipientMobile.trim() || cashPaymentDetails.recipientMobile.length !== 10) {
      toast.error("Please enter valid recipient mobile number (10 digits)");
      return;
    }

    // Store cash payment details in sessionStorage (include selected plan summary)
    const amount = selectedPackageDetails
      ? calculatePrice(selectedPackageDetails.basePrice, packageType)
      : 0;
    sessionStorage.setItem(
      "cashPaymentDetails",
      JSON.stringify({
        ...cashPaymentDetails,
        packageId: selectedPackage,
        packageName: selectedPackageDetails?.name || selectedPackage,
        packageType,
        amount,
      })
    );
    
    // Close cash payment dialog and show super admin authentication
    setShowCashPaymentDialog(false);
    setShowSuperAdminAuth(true);
  };

  const handlePaymentProofUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const maxSizeMb = 5;
    if (file.size > maxSizeMb * 1024 * 1024) {
      toast.error(`Proof file must be under ${maxSizeMb}MB`);
      event.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setCashPaymentDetails((prev) => ({
        ...prev,
        paymentProofName: file.name,
        paymentProofData: typeof reader.result === "string" ? reader.result : "",
      }));
    };
    reader.onerror = () => {
      toast.error("Failed to read proof file. Please try again.");
    };
    reader.readAsDataURL(file);
  };

  const handleSuperAdminAuth = async () => {
    if (!superAdminCredentials.username.trim() || !superAdminCredentials.password.trim()) {
      setAuthError("Please enter both username and password");
      return;
    }

    setAuthLoading(true);
    setAuthError("");

    try {
      // Authenticate super admin using login API
      // Username is mobile number, password is the password
      const response = await fetch(`${USERMGMT_URL}/loginWithMobile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mobile: superAdminCredentials.username,
          password: superAdminCredentials.password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.msg || errorData.message || "Authentication failed");
      }

      const { user } = await response.json();
      
      // Check if user is superadmin
      const role = (user?.roleName || user?.role || "").toLowerCase();
      if (role !== "superadmin" && role !== "super_admin") {
        throw new Error("Only super admin can authenticate cash payments");
      }

      // Authentication successful
      toast.success("Authentication successful");
      
      // Store selected package, package type, and payment method in sessionStorage
      sessionStorage.setItem("selectedPackage", selectedPackage);
      sessionStorage.setItem("packageType", packageType);
      sessionStorage.setItem("paymentMethod", paymentMethod);
      sessionStorage.setItem("cashPaymentAuthenticated", "true");

      // Close dialog and navigate to onboarding
      setShowSuperAdminAuth(false);
      navigate("/onboarding");
    } catch (error) {
      console.error("Super admin authentication error:", error);
      setAuthError(error.message || "Authentication failed. Please check your credentials.");
    } finally {
      setAuthLoading(false);
    }
  };

  return (
<Box
  sx={{
    minHeight: "100vh",
    overflow: "auto",
    bgcolor: HMS_PAGE_BG,
    backgroundImage: `
      radial-gradient(circle at top left, ${HMS_BLUE_LIGHT} 0%, transparent 35%),
      linear-gradient(180deg, #ffffff 0%, ${HMS_PAGE_BG} 100%)
    `,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  }}
>
      <Container
        maxWidth="lg"
        sx={{
          py: 1.25,
          px: { xs: 2, sm: 3 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          overflow: "hidden",
          minHeight: 0,
          maxHeight: "100%",
        }}
      >
        <Box sx={{ textAlign: "center", mb: 2.5, flexShrink: 0 }}>

          <Typography
            component="h1"
            sx={{
              color: HMS_TEXT,
              fontWeight: 800,
              letterSpacing: "-0.025em",
              lineHeight: 1.2,
              fontSize: { xs: "1.45rem", sm: "1.7rem" },
            }}
          >
            Hospital Management System
          </Typography>

          <Typography
            sx={{
              mt: 0.75,
              color: HMS_TEXT_GREY,
              fontSize: { xs: "0.82rem", sm: "0.9rem" },
            }}
          >
            Choose the right plan for your hospital
          </Typography>
        </Box>

        {/* Package Type Selection (Monthly, Quarterly, Yearly) */}
        <Paper
          elevation={0}
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: 0.65,
            mb: 1.5,
            flexShrink: 0,
            border: `1px solid ${HMS_BORDER}`,
            borderRadius: "12px",
            bgcolor: "#FFFFFF",
            boxShadow: "0 2px 8px rgba(15, 23, 42, 0.03)",
          }}
        >
          <FormControl component="fieldset" fullWidth>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: { xs: 0.5, sm: 2 },
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  color: HMS_TEXT,
                  whiteSpace: "nowrap",
                }}
              >
                Billing Cycle
              </Typography>
              <RadioGroup
                row
                value={packageType}
                onChange={(e) => setPackageType(e.target.value)}
                sx={{
                  flexWrap: "nowrap",
                  gap: 0.5,
                  "& .MuiFormControlLabel-root": { mr: 1.5, ml: 0 },
                }}
              >
                <FormControlLabel
                  value="MONTHLY"
                  control={<Radio size="small" />}
                  label={<Typography variant="body2" sx={{ fontSize: "0.8rem" }}>Monthly</Typography>}
                />
                <FormControlLabel
                  value="QUARTERLY"
                  control={<Radio size="small" />}
                  label={<Typography variant="body2" sx={{ fontSize: "0.8rem" }}>Quarterly</Typography>}
                />
                <FormControlLabel
                  value="YEARLY"
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="body2" sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                      Yearly — Best Value
                    </Typography>
                  }
                />
              </RadioGroup>
            </Box>
          </FormControl>
        </Paper>

        {/* Subscription Packages */}
        {loadingPackages ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4, flexShrink: 0 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Grid
              container
              spacing={1}
              sx={{
                mb: 1,
                flexShrink: 0,
                height: { xs: "auto", md: 430 },
                maxHeight: { xs: "none", md: 430 },
                alignItems: "stretch",
                overflow: "visible",
              }}
            >
            {packages.map((pkg) => {
              const price = calculatePrice(pkg.basePrice, packageType);
              const isSelected = selectedPackage === pkg.id;
              const billingSuffix =
                packageType === "MONTHLY"
                  ? "/mo"
                  : packageType === "QUARTERLY"
                  ? "/qtr"
                  : "/yr";

              return (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={pkg.id}
                  sx={{
                    display: "flex",
                    minHeight: 0,
                    height: "100%",
                  }}
                >
                  <Card
  elevation={0}
  sx={{
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    borderRadius: "14px",

    border: isSelected
      ? `2px solid ${HMS_BLUE}`
      : `1px solid ${HMS_BORDER}`,

    boxShadow: isSelected
      ? "0 8px 24px rgba(11,120,208,0.12)"
      : "0 2px 8px rgba(15,23,42,0.04)",

    transition: "all 0.2s ease",
    bgcolor: "#FFFFFF",
    overflow: "hidden",

    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 10px 26px rgba(11,120,208,0.10)",
      borderColor: HMS_BLUE,
    },
  }}
>
                    <Box
                      sx={{
                        height: 3,
                        bgcolor: isSelected ? HMS_BLUE : HMS_BLUE_LIGHT,
                        flexShrink: 0,
                      }}
                    />
                    {pkg.id === "intermediate" && (
                      <Chip
                        label="Most Popular"
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          height: 18,
                          fontSize: "0.65rem",
                          fontWeight: 600,
                          bgcolor: pkg.color,
                          color: "#fff",
                        }}
                      />
                    )}
                    <CardContent
                      sx={{
                        p: 1,
                        pt: 0.7,
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        minHeight: 0,
                        overflow: "hidden",
                        "&:last-child": { pb: 0.75 },
                      }}
                    >
                      <Typography
                        component="h2"
                        fontWeight={700}
                        sx={{
                          color: HMS_TEXT,
                          textTransform: "capitalize",
                          letterSpacing: "0.01em",
                          fontSize: "0.95rem",
                          lineHeight: 1.2,
                          pr: pkg.id === "intermediate" ? 9 : 0,
                          flexShrink: 0,
                        }}
                      >
                        {pkg.name}
                      </Typography>

                      <Box sx={{ mt: 0.3, mb: 1.25, flexShrink: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.4 }}>
                          <Typography
                            component="span"
                            sx={{
                              fontSize: "1.15rem",
                              fontWeight: 700,
                              color: HMS_BLUE,
                              lineHeight: 1.1,
                            }}
                          >
                            ₹{price.toLocaleString("en-IN")}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 500, fontSize: "0.68rem" }}
                          >
                            {billingSuffix}
                          </Typography>
                        </Box>
                        {packageType !== "MONTHLY" && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mt: 0.05, fontSize: "0.62rem" }}
                          >
                            ₹{pkg.basePrice.toLocaleString("en-IN")}/month billed{" "}
                            {packageType === "QUARTERLY" ? "quarterly" : "annually"}
                          </Typography>
                        )}
                      </Box>

                      <Box
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          overflow: "visible",
                          mb: 0.25,
                          mt: 0.15,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "flex-start",
                          gap: 0.2,
                        }}
                      >
                        {pkg.features && pkg.features.length > 0 ? (
                          pkg.features.map((feature, index) => (
                            <Box
                              key={index}
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                flexShrink: 0,
                              }}
                            >
                              <CheckCircleIcon
                                sx={{
                                  color: HMS_BLUE,
                                  mr: 0.75,
                                  fontSize: 15,
                                  mt: "1px",
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                sx={{
                                  fontSize: "0.68rem",
                                  color: HMS_TEXT_GREY,
                                  lineHeight: 1.25,
                                }}
                              >
                                {feature}
                              </Typography>
                            </Box>
                          ))
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No features listed
                          </Typography>
                        )}
                      </Box>

                      <Button
                        fullWidth
                        size="small"
                        variant={isSelected ? "contained" : "outlined"}
                        onClick={() => setSelectedPackage(pkg.id)}
                        sx={{
                          mt: "auto",
                          flexShrink: 0,
                          textTransform: "none",
                          fontWeight: 700,
                          py: 0.65,
                          fontSize: "0.75rem",
                          minHeight: 34,
                          borderRadius: "8px",
                        
                          bgcolor: isSelected ? HMS_BLUE : "transparent",
                          color: isSelected ? "#fff" : HMS_BLUE,
                          borderColor: HMS_BLUE,
                        
                          "&:hover": {
                            bgcolor: isSelected ? HMS_BLUE_DARK : HMS_BLUE_LIGHT,
                            color: isSelected ? "#fff" : HMS_BLUE_DARK,
                            borderColor: HMS_BLUE,
                          },
                        }}
                      >
                        {isSelected ? "Selected" : "Select Plan"}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Payment Method Selection */}
        <Paper
          elevation={0}
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: 0.65,
            mb: 1.5,
            flexShrink: 0,
            border: `1px solid ${HMS_BORDER}`,
            borderRadius: "12px",
            bgcolor: "#FFFFFF",
            boxShadow: "0 2px 8px rgba(15, 23, 42, 0.03)",
          }}
        >
          <FormControl component="fieldset" fullWidth>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: { xs: 0.5, sm: 2 },
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  color: HMS_TEXT,
                  whiteSpace: "nowrap",
                }}
              >
                Payment Method
              </Typography>
              <RadioGroup
                row
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                sx={{
                  flexWrap: "nowrap",
                  gap: 0.5,
                  "& .MuiFormControlLabel-root": { mr: 1.5, ml: 0 },
                }}
              >
                <FormControlLabel
                  value="online"
                  control={<Radio size="small" />}
                  label={<Typography variant="body2" sx={{ fontSize: "0.8rem" }}>Online Payment</Typography>}
                />
                <FormControlLabel
                  value="cash"
                  control={<Radio size="small" />}
                  label={<Typography variant="body2" sx={{ fontSize: "0.8rem" }}>Cash Payment</Typography>}
                />
              </RadioGroup>
            </Box>
          </FormControl>
        </Paper>

        {/* Get Started Button */}
        <Box sx={{ textAlign: "center", flexShrink: 0, mb: 0.75 }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleGetStarted}
            disabled={loading}
            sx={{
              px: 4,
              py: 0.75,
              fontSize: "0.85rem",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "9px",
              minHeight: 38,
            
              bgcolor: HMS_BLUE,
              color: "#FFFFFF",
            
              boxShadow: "0 5px 14px rgba(11,120,208,0.22)",
            
              "&:hover": {
                bgcolor: HMS_BLUE_DARK,
                boxShadow: "0 7px 18px rgba(11,120,208,0.28)",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              "Get Started"
            )}
          </Button>
        </Box>

        {/* Already have an account? */}
        <Box sx={{ textAlign: "center", flexShrink: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
            Already have an account?{" "}
            <Button
              variant="text"
              size="small"
              onClick={() => navigate("/login")}
              sx={{
                textTransform: "none",
                color: HMS_BLUE,
                fontWeight: 700,
                p: 0,
                minWidth: "auto",
                verticalAlign: "baseline",
                fontSize: "0.8rem",
                "&:hover": {
                  bgcolor: "transparent",
                  color: HMS_BLUE_DARK,
                },
              }}
            >
              Sign In
            </Button>
          </Typography>
        </Box>
      </Container>

      {/* Cash Payment Details Dialog */}
      <Dialog
        open={showCashPaymentDialog}
        onClose={() => setShowCashPaymentDialog(false)}
        maxWidth="md"
        fullWidth
        sx={CASH_PAYMENT_DIALOG_PAPER_SX}
      >
        <DialogTitle sx={STANDARD_FORM_DIALOG_TITLE_SX}>
          Cash Payment Details
        </DialogTitle>
        <DialogContent sx={FIT_CONTENT_DIALOG_CONTENT_SX}>
          {selectedPackageDetails && (
            <Box
              sx={{
                mb: 1,
                px: 1.25,
                py: 0.75,
                borderRadius: 1.5,
                bgcolor: HMS_BLUE_LIGHT,
                border: `1px solid ${HMS_BORDER}`,
                textAlign: "center",
              }}
            >
              <Typography sx={{ fontSize: "0.85rem", color: HMS_BLUE, fontWeight: 600 }}>
                You&apos;re paying{" "}
                <Box component="span" sx={{ color: selectedPackageDetails.color || "#0d9488" }}>
                  ₹
                  {calculatePrice(selectedPackageDetails.basePrice, packageType).toLocaleString(
                    "en-IN"
                  )}
                </Box>{" "}
                for the{" "}
                <Box component="span" sx={{ textTransform: "capitalize" }}>
                  {selectedPackageDetails.name}
                </Box>{" "}
                package
                {" "}(
                {packageType === "MONTHLY"
                  ? "Monthly"
                  : packageType === "QUARTERLY"
                  ? "Quarterly"
                  : "Yearly"}
                )
              </Typography>
            </Box>
          )}

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1, textAlign: "center", fontSize: "0.8rem" }}
          >
            Please provide the details of who is paying and to whom for cash payment authentication.
          </Typography>
          <Divider sx={{ mb: 1 }} />

          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                Payer Details
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Payer Name"
                value={cashPaymentDetails.payerName}
                onChange={(e) =>
                  setCashPaymentDetails({
                    ...cashPaymentDetails,
                    payerName: e.target.value,
                  })
                }
                required
                {...DENSE_FIELD_PROPS}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Payer Mobile Number"
                value={cashPaymentDetails.payerMobile}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                  setCashPaymentDetails({
                    ...cashPaymentDetails,
                    payerMobile: value,
                  });
                }}
                inputProps={{ maxLength: 10 }}
                required
                {...DENSE_FIELD_PROPS}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, mt: 0.25, fontWeight: 600 }}>
                Recipient Details
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Recipient Name"
                value={cashPaymentDetails.recipientName}
                onChange={(e) =>
                  setCashPaymentDetails({
                    ...cashPaymentDetails,
                    recipientName: e.target.value,
                  })
                }
                required
                {...DENSE_FIELD_PROPS}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Recipient Mobile Number"
                value={cashPaymentDetails.recipientMobile}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                  setCashPaymentDetails({
                    ...cashPaymentDetails,
                    recipientMobile: value,
                  });
                }}
                inputProps={{ maxLength: 10 }}
                required
                {...DENSE_FIELD_PROPS}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, mt: 0.25, fontWeight: 600 }}>
                Comment / Proof of Payment
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                Optional — e.g. full cash, or half cash + half online. You can upload a transaction
                screenshot or any payment proof.
              </Typography>
              <TextField
                fullWidth
                label="Payment Comment / Notes"
                placeholder="Example: Paid ₹5,000 cash and remaining amount online"
                value={cashPaymentDetails.paymentComment}
                onChange={(e) =>
                  setCashPaymentDetails({
                    ...cashPaymentDetails,
                    paymentComment: e.target.value,
                  })
                }
                {...DENSE_FIELD_PROPS}
              />
              <Box
                sx={{
                  mt: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  color="primary"
                  sx={FORM_CANCEL_BUTTON_SX}
                >
                  Upload Proof
                  <input
                    type="file"
                    hidden
                    accept="image/*,.pdf"
                    onChange={handlePaymentProofUpload}
                  />
                </Button>
                {cashPaymentDetails.paymentProofName ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
                    {cashPaymentDetails.paymentProofName}
                    <Button
                      size="small"
                      onClick={() =>
                        setCashPaymentDetails({
                          ...cashPaymentDetails,
                          paymentProofName: "",
                          paymentProofData: "",
                        })
                      }
                      sx={{ textTransform: "none", ml: 0.5, minWidth: "auto", p: 0 }}
                    >
                      Remove
                    </Button>
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Image or PDF (max 5MB)
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={STANDARD_FORM_DIALOG_ACTIONS_SX}>
          <Button onClick={() => setShowCashPaymentDialog(false)} variant="outlined" color="primary" sx={FORM_CANCEL_BUTTON_SX}>Cancel</Button>
          <Button
            onClick={handleCashPaymentDetailsSubmit}
            variant="contained"
            color="primary"
            sx={FORM_SUBMIT_BUTTON_SX}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Super Admin Authentication Dialog */}
      <Dialog
        open={showSuperAdminAuth}
        onClose={() => setShowSuperAdminAuth(false)}
        maxWidth="md"
        fullWidth
        sx={STANDARD_FORM_DIALOG_PAPER_SX}
      >
        <DialogTitle sx={STANDARD_FORM_DIALOG_TITLE_SX}>Super Admin Authentication</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please authenticate with super admin credentials to proceed with cash payment.
          </Typography>
          {authError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {authError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Username (Mobile Number)"
                value={superAdminCredentials.username}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                  setSuperAdminCredentials({
                    ...superAdminCredentials,
                    username: value,
                  });
                  setAuthError("");
                }}
                inputProps={{ maxLength: 10 }}
                required
                helperText="Enter super admin mobile number"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={superAdminCredentials.password}
                onChange={(e) => {
                  setSuperAdminCredentials({
                    ...superAdminCredentials,
                    password: e.target.value,
                  });
                  setAuthError("");
                }}
                required
                helperText="Enter super admin password"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={STANDARD_FORM_DIALOG_ACTIONS_SX}>
          <Button
            onClick={() => {
              setShowSuperAdminAuth(false);
              setShowCashPaymentDialog(true);
            }}
            disabled={authLoading}
            variant="outlined"
            color="primary"
            sx={FORM_CANCEL_BUTTON_SX}
          >
            Back
          </Button>
          <Button
            onClick={handleSuperAdminAuth}
            variant="contained"
            color="primary"
            disabled={authLoading}
            sx={FORM_SUBMIT_BUTTON_SX}
          >
            {authLoading ? <CircularProgress size={24} /> : "Authenticate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HomePage;

